import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { path } from 'ramda'
import { Link } from 'react-router-dom'
import { useFirestoreConnect } from 'react-redux-firebase'
import styled from 'styled-components'
import Divider from '@material-ui/core/Divider'
import Type from '../../Components/Type'
import Tabs from '../../Components/Tabs'
import L from '../../Components/L'
import {
  WEBSITE_CONFIG_COLLECTION,
  WEBSITE_CONFIG_ID,
  COLLECTIONS_COLLECTION,
  SIMULATIONS_COLLECTION,
} from '../../Config'
import IconArrowLeftDark from '../../Icons/IconArrowLeftDark'
import IconArrowRightDark from '../../Icons/IconArrowRightDark'
import IconMXDetails from '../../Icons/IconMXDetails'
import IconEmbed from '../../Icons/IconEmbed'
import Layout from '../../Components/Layout'
import LoginSSO from '../LoginSSO'
import {
  SimulationImg,
  SimulationDescription,
  StatusDot,
  StyledSimulation,
  StyledSimIcon,
} from './MXCollectionPage-styles'

export const Status = props => (
  <L display="grid" flow="column" gap={1} jc="center" ai="center">
    <StatusDot status={props.status === 'active' ? 'success' : 'disabledDark'} />
    <Type variant="h4" color={props.status === 'active' ? 'success' : 'disabledDark'}>
      {props.status === 'active' ? 'Active' : 'No Signal'}
    </Type>
  </L>
)

const SimIcon = props => (
  <StyledSimIcon label={props.label}>
    <Link to={props.to}>{props.icon}</Link>
  </StyledSimIcon>
)

const Simulation = props => (
  <StyledSimulation>
    <Link to={`/marketing-experience/${props.collectionID}/${props.id}`}>
      <SimulationImg bgImg={path(['thumbnail', 'signedUrl'], props)} />
    </Link>
    <L display="grid" gap={1.5} maxX="100%" width={{ md: '350px' }}>
      <Link to={`/marketing-experience/${props.collectionID}/${props.id}`}>
        <Type variant="titleMedium" className="simulation__title">
          {props.name}
        </Type>
      </Link>
      <SimulationDescription variant="subtitle">
        <span
          dangerouslySetInnerHTML={{
            __html: props.description ? props.description : 'No description available',
          }}
        />
      </SimulationDescription>
    </L>
    {/* <Status status="active" /> */}
    <L display={{ sm: 'none' }} alignSelf="center" position={{ xx: 'absolute', xs: 'static' }} t="47px" r="0">
      {IconArrowRightDark && <IconArrowRightDark />}
      {/* <IconArrowRightDark /> */}
    </L>
    <L display={{ xx: 'none', sm: 'grid' }} flow="column" gap={3} ai="center" color="secondaryHue">
      <SimIcon
        to={`/marketing-experience/${props.collectionID}/${props.id}#details`}
        icon={IconMXDetails && <IconMXDetails />}
        label="Details"
      />
      <SimIcon
        to={`/marketing-experience/${props.collectionID}/${props.id}#embed`}
        icon={IconEmbed && <IconEmbed />}
        label="Embed"
      />
    </L>
    <L display={{ sm: 'none' }} position={{ xx: 'absolute' }} t="0" l="0" width="100%" height="100%">
      <Link
        to={`/marketing-experience/${props.collectionID}/${props.id}`}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    </L>
  </StyledSimulation>
)

const StyledTabs = styled.div`
  box-shadow: ${props => props.theme.globals.boxShadowSmaller};
`

export const SORT = {
  alpha_desc: {
    name: 'Name A-Z',
    fn: (a, b) => {
      a = a.toLowerCase()
      b = b.toLowerCase()
      return a < b ? -1 : a > b ? 1 : 0
    },
  },
  alpha_asc: {
    name: 'Name Z-A',
    fn: (a, b) => {
      a = a.toLowerCase()
      b = b.toLowerCase()
      return b < a ? -1 : b > a ? 1 : 0
    },
  },
}

const SortArrow = styled.div`
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid ${props => props.theme.palette.primary.main};
  transform-origin: center;
  transition: ${props => props.theme.mixins.transition(['transform'])};
  transform: rotate(${props => (props.active ? '-180deg' : '0deg')});
`

const SortBody = styled(L).attrs({
  position: 'absolute',
  t: 'calc(100% + 5px)',
  l: { xx: '0', md: 'auto' },
  r: { md: '0' },
  width: 'auto',
  minX: '150px',
  z: 'max',
})`
  overflow: hidden;
  border-radius: ${props => props.theme.globals.borderRadiusSmall}px;
  box-shadow: ${props => props.theme.globals.depth[300]};
  background-color: ${props => props.theme.palette.bg.paper.main};
  transition: ${props => props.theme.mixins.transition(['opacity', 'visibility'])};
  opacity: ${props => (props.active ? 1 : 0)};
  visibility: ${props => (props.active ? 'visible' : 'hidden')};
`

const SortItem = styled(L).attrs(props => ({
  pY: 1,
  pX: 1.5,
  bgColor: props.active ? 'paperHover' : 'paper',
}))`
  transition: ${props => props.theme.mixins.transition(['background-color'])};

  &:hover {
    background-color: ${props => props.theme.palette.bg.paperHover.main};
  }
`

const SortScrim = styled(L).attrs(props => ({
  position: 'fixed',
  t: '0',
  l: '0',
  width: '100%',
  height: '100%',
  z: 'overlay',
  display: props.active ? 'block' : 'none',
}))``

export const Sort = props => {
  const [sortType, setSortType] = useState('alpha_desc')
  const [active, setActive] = useState(false)
  return (
    <L position="relative" selectable>
      <SortScrim active={active} onMouseUp={() => setActive(false)} />
      <L display="grid" flow="column" ai="center" gap={1} onMouseUp={() => setActive(!active)}>
        <Type variant="body2" color="tertiary">
          Sort By: <span style={{ fontWeight: '800' }}>{SORT[sortType].name}</span>
        </Type>
        <SortArrow active={active} />
      </L>
      <SortBody active={active}>
        {Object.keys(SORT).map(sort => (
          <SortItem
            key={`sort_${sort}`}
            active={sort === sortType}
            onMouseUp={e => {
              e.stopPropagation()
              setSortType(sort)
              props.onChange(sort)
            }}
          >
            <Type variant="body2" color="tertiary">
              {SORT[sort].name}
            </Type>
          </SortItem>
        ))}
      </SortBody>
    </L>
  )
}

const MXCollection = ({
  match: {
    params: { collectionId },
  },
}) => {
  useFirestoreConnect([
    {
      collection: WEBSITE_CONFIG_COLLECTION,
      where: ['id', '==', WEBSITE_CONFIG_ID],
    },
    {
      // Load ALL Colletions
      collection: COLLECTIONS_COLLECTION,
      orderBy: ['weight'],
    },
    {
      // Load ALL Simulations
      collection: SIMULATIONS_COLLECTION,
    },
  ])

  const { data, ordered } = useSelector(({ firestore }) => firestore) || {}
  const config = data?.[WEBSITE_CONFIG_COLLECTION]?.[WEBSITE_CONFIG_ID]
  const collections = ordered?.[COLLECTIONS_COLLECTION] || {}
  const simulations = data?.[SIMULATIONS_COLLECTION] || {}
  const { auth, profile } = useSelector(({ firebase }) => firebase) || {}
  const { claims } = useSelector(({ account }) => account) || {}

  const [sortType, setSortType] = useState('alpha_desc')

  const tabs = [
    { link: '/', label: 'Sales Experience' },
    { link: '/marketing-experience', label: 'Marketing Experience' },
  ]

  useEffect(() => {
    document.title = 'IdeaCloud | Marketing Experience Collections'
  }, [])

  if (!auth.isLoaded || !profile.isLoaded || claims === null || !config) return null
  if (auth.isEmpty) return <LoginSSO />
  if (!collections || !simulations) return null
  let collection = null
  for (let i = 0; i < collections.length; i++) {
    if (collections[i].id === collectionId) collection = collections[i]
  }
  if (!collection) return null

  return (
    <Layout title="Marketing Experience" hide full backgroundColor="#F9F9FB">
      <StyledTabs>
        <Tabs tab="/marketing-experience" tabs={tabs} />
      </StyledTabs>
      <L container display="grid" gap={3} pY={3}>
        <L display="grid" gap={2}>
          <L display="inline-grid" columns="max-content">
            <Link to="/marketing-experience">
              <L display="grid" flow="column" ai="center" jc="flex-start" selectable color="secondaryHueLightish">
                <IconArrowLeftDark />
                <Type variant="subtitle">Back</Type>
              </L>
            </Link>
          </L>
          <L display="grid" flow={{ md: 'column' }} gap={{ xx: 2, md: 3 }} ai="center" jc="space-between">
            <Type variant="titleLarge">{collection.name}</Type>
            <Sort onChange={setSortType} />
          </L>
        </L>
        <L>
          {collection &&
            collection.simulations
              .slice()
              .sort(SORT[sortType].fn)
              .map(
                simId =>
                  simulations[simId] && (
                    <div key={simId}>
                      <Divider light />
                      <Simulation collectionID={collection.id} {...simulations[simId]} />
                    </div>
                  ),
              )}
          <Divider light />
        </L>
      </L>
    </Layout>
  )
}

export default MXCollection
